import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CreditCard, Plus, Clock, CheckCircle, XCircle, AlertTriangle, Trash2, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Subscription {
  id: string;
  landlord_id: string;
  plan_type: string;
  price: number;
  properties_limit: number;
  start_date: string;
  end_date: string;
  status: string;
  created_at: string;
  landlords: {
    full_name: string;
    email: string;
  };
}

interface PendingLandlord {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
  phone: string;
  is_active: boolean;
  created_at: string;
}

const SubscriptionsManagement = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [pendingLandlords, setPendingLandlords] = useState<PendingLandlord[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedLandlord, setSelectedLandlord] = useState<string>('');
  const [newSubscription, setNewSubscription] = useState({
    plan_type: 'basic',
    duration_months: 1
  });
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, [sortOrder]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch active subscriptions
      const { data: subsData, error: subsError } = await supabase
        .from('subscriptions')
        .select(`
          *,
          landlords!inner(
            full_name,
            email
          )
        `)
        .order('created_at', { ascending: sortOrder === 'oldest' });

      if (subsError) throw subsError;
      setSubscriptions(subsData || []);

      // Fetch landlords ready for subscription activation (email verified => is_active=true) but not yet subscribed (is_verified=false)
      const { data: pendingData, error: pendingError } = await supabase
        .from('landlords')
        .select('*')
        .eq('is_active', true)
        .eq('is_verified', false)
        .order('created_at', { ascending: false });

      if (pendingError) throw pendingError;
      setPendingLandlords(pendingData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء جلب البيانات",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const activateSubscription = async () => {
    if (!selectedLandlord) return;

    try {
      // الحصول على بيانات المالك
      const { data: landlordData, error: landlordError } = await supabase
        .from('landlords')
        .select('email')
        .eq('id', selectedLandlord)
        .maybeSingle();

      if (landlordError || !landlordData) {
        throw new Error('لم يتم العثور على بيانات المالك');
      }

      // تفعيل الاشتراك باستخدام الدالة الجديدة
      const { data, error } = await supabase.rpc('activate_landlord_subscription_with_verification', {
        p_landlord_id: selectedLandlord,
        p_plan_type: newSubscription.plan_type,
        p_duration_months: newSubscription.duration_months
      });

      if (error) throw error;

      // إرسال رسالة تفعيل البريد الإلكتروني
      try {
        const { data: session } = await supabase.auth.getSession();
        const response = await supabase.functions.invoke('send-verification-email', {
          body: { email: landlordData.email },
          headers: {
            Authorization: `Bearer ${session?.session?.access_token}`
          }
        });

        if (response.error) {
          console.warn('خطأ في إرسال رسالة التفعيل:', response.error);
          toast({
            title: "تحذير",
            description: "تم تفعيل الاشتراك لكن فشل في إرسال رسالة التفعيل",
            variant: "default"
          });
        }
      } catch (emailError) {
        console.warn('خطأ في إرسال رسالة التفعيل:', emailError);
      }

      toast({
        title: "تم التفعيل",
        description: "تم تفعيل اشتراك المالك وإرسال رسالة التفعيل بنجاح",
        variant: "default"
      });

      setIsDialogOpen(false);
      setSelectedLandlord('');
      setNewSubscription({ plan_type: 'basic', duration_months: 1 });
      fetchData();
    } catch (error: any) {
      console.error('Error activating subscription:', error);
      toast({
        title: "خطأ في التفعيل",
        description: error.message || "حدث خطأ أثناء تفعيل الاشتراك",
        variant: "destructive"
      });
    }
  };

  const deactivateLandlord = async (landlordId: string) => {
    try {
      // Deactivate landlord
      const { error: landlordError } = await supabase
        .from('landlords')
        .update({ is_active: false })
        .eq('id', landlordId);

      if (landlordError) throw landlordError;

      // Expire active subscriptions
      const { error: subsError } = await supabase
        .from('subscriptions')
        .update({ status: 'expired' })
        .eq('landlord_id', landlordId)
        .eq('status', 'active');

      if (subsError) throw subsError;

      toast({
        title: "تم إيقاف الحساب",
        description: "تم إيقاف حساب المالك وإنهاء اشتراكه",
        variant: "default"
      });

      fetchData();
    } catch (error: any) {
      console.error('Error deactivating landlord:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء إيقاف الحساب",
        variant: "destructive"
      });
    }
  };

  const deleteLandlord = async (landlordId: string, userId: string) => {
    try {
      // Delete landlord record (this will cascade delete subscriptions)
      const { error: landlordError } = await supabase
        .from('landlords')
        .delete()
        .eq('id', landlordId);

      if (landlordError) throw landlordError;

      toast({
        title: "تم الحذف",
        description: "تم حذف المالك نهائياً من النظام",
        variant: "default"
      });

      fetchData();
    } catch (error: any) {
      console.error('Error deleting landlord:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء حذف المالك",
        variant: "destructive"
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB'); // التنسيق الميلادي
  };

  const getDaysRemaining = (endDate: string) => {
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getDaysRemainingBadge = (daysRemaining: number) => {
    if (daysRemaining <= 0) {
      return <Badge variant="destructive">منتهي</Badge>;
    } else if (daysRemaining <= 7) {
      return <Badge variant="secondary" className="bg-orange-100 text-orange-800">{daysRemaining} يوم</Badge>;
    } else if (daysRemaining <= 30) {
      return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">{daysRemaining} يوم</Badge>;
    } else {
      return <Badge variant="default" className="bg-green-100 text-green-800">{daysRemaining} يوم</Badge>;
    }
  };

  const getPlanLabel = (planType: string) => {
    switch (planType) {
      case 'basic': return 'أساسية';
      case 'standard': return 'متوسطة';
      case 'premium': return 'متقدمة';
      default: return planType;
    }
  };

  const activeSubscriptions = subscriptions.filter(s => s.status === 'active');
  const expiredSubscriptions = subscriptions.filter(s => s.status === 'expired');

  if (loading) {
    return <div className="text-center py-8">جاري التحميل...</div>;
  }

  return (
    <div className="space-y-6">
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            إدارة الاشتراكات والملاك
          </CardTitle>
          <CardDescription>
            عرض وإدارة اشتراكات الملاك وطلبات تفعيل الاشتراك (بعد تأكيد البريد)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="active" className="space-y-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="pending">طلبات تفعيل الاشتراك ({pendingLandlords.length})</TabsTrigger>
              <TabsTrigger value="active">الاشتراكات النشطة ({activeSubscriptions.length})</TabsTrigger>
              <TabsTrigger value="expired">الاشتراكات المنتهية ({expiredSubscriptions.length})</TabsTrigger>
            </TabsList>

            {/* طلبات تفعيل الاشتراك */}
            <TabsContent value="pending">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">الملاك في انتظار تفعيل الاشتراك</h3>
                </div>
                
                {pendingLandlords.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    لا توجد طلبات تفعيل اشتراك جديدة
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-right">اسم المالك</TableHead>
                          <TableHead className="text-right">البريد الإلكتروني</TableHead>
                          <TableHead className="text-right">رقم الهاتف</TableHead>
                          <TableHead className="text-right">تاريخ التسجيل</TableHead>
                          <TableHead className="text-right">الإجراءات</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pendingLandlords.map((landlord) => (
                          <TableRow key={landlord.id}>
                            <TableCell className="text-right font-medium">{landlord.full_name}</TableCell>
                            <TableCell className="text-right">{landlord.email}</TableCell>
                            <TableCell className="text-right">{landlord.phone || 'غير محدد'}</TableCell>
                            <TableCell className="text-right">{formatDate(landlord.created_at)}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center gap-2">
                                <Dialog open={isDialogOpen && selectedLandlord === landlord.id} onOpenChange={(open) => {
                                  setIsDialogOpen(open);
                                  if (!open) setSelectedLandlord('');
                                }}>
                                  <DialogTrigger asChild>
                                    <Button 
                                      size="sm" 
                                      onClick={() => setSelectedLandlord(landlord.id)}
                                      className="bg-green-600 hover:bg-green-700"
                                    >
                                      <CheckCircle className="h-4 w-4" />
                                      تفعيل
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent>
                                    <DialogHeader>
                                      <DialogTitle>تفعيل اشتراك المالك</DialogTitle>
                                      <DialogDescription>
                                        اختر نوع الباقة ومدة الاشتراك للمالك: {landlord.full_name}
                                      </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4">
                                      <div>
                                        <Label>نوع الباقة</Label>
                                        <Select value={newSubscription.plan_type} onValueChange={(value) => 
                                          setNewSubscription({...newSubscription, plan_type: value})
                                        }>
                                          <SelectTrigger>
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="basic">أساسية (5 ريال - عقار واحد)</SelectItem>
                                            <SelectItem value="standard">متوسطة (10 ريال - 3 عقارات)</SelectItem>
                                            <SelectItem value="premium">متقدمة (20 ريال - 7 عقارات)</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div>
                                        <Label>مدة الاشتراك (بالأشهر)</Label>
                                        <Input 
                                          type="number" 
                                          min="1" 
                                          max="12"
                                          value={newSubscription.duration_months}
                                          onChange={(e) => 
                                            setNewSubscription({...newSubscription, duration_months: parseInt(e.target.value) || 1})
                                          }
                                        />
                                      </div>
                                      <Button onClick={activateSubscription} className="w-full">
                                        تفعيل الاشتراك
                                      </Button>
                                    </div>
                                  </DialogContent>
                                </Dialog>
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => deleteLandlord(landlord.id, landlord.user_id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                  حذف
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* الاشتراكات النشطة */}
            <TabsContent value="active">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">الاشتراكات النشطة</h3>
                  <Select value={sortOrder} onValueChange={(value: 'newest' | 'oldest') => setSortOrder(value)}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">الأحدث أولاً</SelectItem>
                      <SelectItem value="oldest">الأقدم أولاً</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {activeSubscriptions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    لا توجد اشتراكات نشطة
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-right">اسم المالك</TableHead>
                          <TableHead className="text-right">الباقة</TableHead>
                          <TableHead className="text-right">الحد الأقصى للعقارات</TableHead>
                          <TableHead className="text-right">السعر</TableHead>
                          <TableHead className="text-right">تاريخ البداية</TableHead>
                          <TableHead className="text-right">تاريخ الانتهاء</TableHead>
                          <TableHead className="text-right">الأيام المتبقية</TableHead>
                          <TableHead className="text-right">الإجراءات</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activeSubscriptions.map((subscription) => {
                          const daysRemaining = getDaysRemaining(subscription.end_date);
                          return (
                            <TableRow key={subscription.id} className="hover:bg-muted/50">
                              <TableCell className="text-right font-medium">
                                {subscription.landlords.full_name}
                              </TableCell>
                              <TableCell className="text-right">
                                <Badge variant="outline">{getPlanLabel(subscription.plan_type)}</Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <Badge variant="secondary">{subscription.properties_limit} عقار</Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                {subscription.price} ريال
                              </TableCell>
                              <TableCell className="text-right">{formatDate(subscription.start_date)}</TableCell>
                              <TableCell className="text-right">{formatDate(subscription.end_date)}</TableCell>
                            <TableCell className="text-right">{getDaysRemainingBadge(daysRemaining)}</TableCell>
                              <TableCell className="text-right">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => deactivateLandlord(subscription.landlord_id)}
                                  className="hover:bg-destructive hover:text-destructive-foreground"
                                >
                                  <XCircle className="h-4 w-4" />
                                  إيقاف
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* الاشتراكات المنتهية */}
            <TabsContent value="expired">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">الاشتراكات المنتهية</h3>
                
                {expiredSubscriptions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    لا توجد اشتراكات منتهية
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-right">اسم المالك</TableHead>
                          <TableHead className="text-right">الباقة</TableHead>
                          <TableHead className="text-right">السعر</TableHead>
                          <TableHead className="text-right">تاريخ الانتهاء</TableHead>
                          <TableHead className="text-right">الحالة</TableHead>
                          <TableHead className="text-right">الإجراءات</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {expiredSubscriptions.map((subscription) => (
                          <TableRow key={subscription.id} className="hover:bg-muted/50">
                            <TableCell className="text-right font-medium">
                              {subscription.landlords.full_name}
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge variant="outline">{getPlanLabel(subscription.plan_type)}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              {subscription.price} ريال
                            </TableCell>
                            <TableCell className="text-right">{formatDate(subscription.end_date)}</TableCell>
                            <TableCell className="text-right">
                              <Badge variant="destructive">منتهي</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center gap-2">
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => setSelectedLandlord(subscription.landlord_id)}
                                    >
                                      <Plus className="h-4 w-4" />
                                      تجديد
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent>
                                    <DialogHeader>
                                      <DialogTitle>تجديد الاشتراك</DialogTitle>
                                      <DialogDescription>
                                        تجديد اشتراك المالك: {subscription.landlords.full_name}
                                      </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4">
                                      <div>
                                        <Label>نوع الباقة</Label>
                                        <Select value={newSubscription.plan_type} onValueChange={(value) => 
                                          setNewSubscription({...newSubscription, plan_type: value})
                                        }>
                                          <SelectTrigger>
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="basic">أساسية (5 ريال - عقار واحد)</SelectItem>
                                            <SelectItem value="standard">متوسطة (10 ريال - 3 عقارات)</SelectItem>
                                            <SelectItem value="premium">متقدمة (20 ريال - 7 عقارات)</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div>
                                        <Label>مدة الاشتراك (بالأشهر)</Label>
                                        <Input 
                                          type="number" 
                                          min="1" 
                                          max="12"
                                          value={newSubscription.duration_months}
                                          onChange={(e) => 
                                            setNewSubscription({...newSubscription, duration_months: parseInt(e.target.value) || 1})
                                          }
                                        />
                                      </div>
                                      <Button onClick={() => {
                                        setSelectedLandlord(subscription.landlord_id);
                                        activateSubscription();
                                      }} className="w-full">
                                        تجديد الاشتراك
                                      </Button>
                                    </div>
                                  </DialogContent>
                                </Dialog>
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => deleteLandlord(subscription.landlord_id, subscription.landlords.full_name)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                  حذف
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionsManagement;